// Helper functions for parsing, formatting, and validating KPI criteria

export type CriteriaOperator = ">=" | ">" | "<=" | "<" | "=";

export type CriteriaUnit = "%" | "ຄັ້ງ" | "ຄະແນນ" | "ກີບ" | "";

export interface ParsedCriteria {
  operator: CriteriaOperator | null;
  value: number | null;
  unit: CriteriaUnit | null;
  isCustom: boolean;
  originalText: string | null;
}

export interface ValidationResult {
  isValid: boolean;
  error?: string;
  warning?: string;
  parsed?: ParsedCriteria;
}

/**
 * Parse criteria string into structured parts (operator, value, unit)
 * Supports both standard format (">= 90%", "<= 5 ຄັ້ງ") and custom text
 */
export function parseCriteria(
  value: string | null | undefined,
): ParsedCriteria {
  if (!value || value.trim() === "") {
    return {
      operator: null,
      value: null,
      unit: null,
      isCustom: false,
      originalText: null,
    };
  }

  const trimmed = value.trim();

  // Try to parse standard format: operator + number + unit
  // Pattern: (>=|>|<=|<|=) \s* (\d+(?:\.\d+)?) \s* (%|ຄັ້ງ|ຄະແນນ|ກີບ)?
  const standardPattern =
    /^(>=|>|<=|<|=)\s*(\d+(?:\.\d+)?)\s*(%|ຄັ້ງ|ຄະແນນ|ກີບ|)$/;
  const match = trimmed.match(standardPattern);

  if (match?.[1] && match?.[2]) {
    const operator = match[1] as CriteriaOperator;
    const valueNum = Number.parseFloat(match[2]);
    const unit = (match[3] || "") as CriteriaUnit;

    return {
      operator,
      value: valueNum,
      unit,
      isCustom: false,
      originalText: trimmed,
    };
  }

  // If not standard format, treat as custom text
  return {
    operator: null,
    value: null,
    unit: null,
    isCustom: true,
    originalText: trimmed,
  };
}

/**
 * Format structured criteria parts into a string
 */
export function formatCriteria(
  operator: CriteriaOperator | null,
  value: number | null,
  unit: CriteriaUnit | null,
): string {
  if (!operator || value === null) {
    return "";
  }

  const unitStr = unit === "" ? "" : unit === "%" ? "%" : ` ${unit}`;
  return `${operator} ${value}${unitStr}`;
}

/**
 * Map targetUnit enum to CriteriaUnit
 */
export function mapTargetUnitToCriteriaUnit(
  targetUnit:
    | "percent"
    | "count"
    | "score"
    | "currency"
    | "none"
    | null
    | undefined,
): CriteriaUnit | null {
  switch (targetUnit) {
    case "percent":
      return "%";
    case "count":
      return "ຄັ້ງ";
    case "score":
      return "ຄະແນນ";
    case "currency":
      return "ກີບ";
    case "none":
      return "";
    default:
      return null;
  }
}

/**
 * Validate criteria format
 * Returns validation result with parsed data if valid
 */
export function validateCriteriaFormat(
  value: string | null | undefined,
  targetUnit?: "percent" | "count" | "score" | "currency" | "none" | null,
): ValidationResult {
  if (!value || value.trim() === "") {
    return {
      isValid: true, // Empty is valid (optional field)
      parsed: {
        operator: null,
        value: null,
        unit: null,
        isCustom: false,
        originalText: null,
      },
    };
  }

  const trimmed = value.trim();
  const parsed = parseCriteria(trimmed);

  // Custom text validation
  if (parsed.isCustom) {
    // Allow custom text if it contains Lao characters and has reasonable length
    const hasLaoChars = /[\u0E80-\u0EFF]/.test(trimmed);
    const minLength = 5;

    if (trimmed.length < minLength) {
      return {
        isValid: false,
        error: `ຂໍ້ຄວາມສັ້ນເກີນໄປ. ຕ້ອງມີຢ່າງໜ້ອຍ ${minLength} ຕົວອັກສອນ`,
        parsed,
      };
    }

    if (!hasLaoChars && trimmed.length < 10) {
      return {
        isValid: false,
        error: "ຂໍ້ຄວາມບໍ່ສົມບູນ. ກະລຸນາອະທິບາຍຢ່າງຊັດເຈນ (ຕົວຢ່າງ: ຫຼຸດລົງ 10% ຫຼື ຫຼາຍກວ່າ)",
        parsed,
      };
    }

    return {
      isValid: true,
      parsed,
    };
  }

  // Standard format validation
  if (!parsed.operator || parsed.value === null) {
    return {
      isValid: false,
      error: 'ຮູບແບບບໍ່ຖືກຕ້ອງ. ຕົວຢ່າງ: ">= 90%", "<= 5 ຄັ້ງ", "= 4.0 ຄະແນນ"',
      parsed,
    };
  }

  // Check unit consistency with targetUnit if provided
  if (targetUnit && parsed.unit) {
    const expectedUnit = mapTargetUnitToCriteriaUnit(targetUnit);
    if (expectedUnit && parsed.unit !== expectedUnit) {
      return {
        isValid: true, // Still valid, but warn
        warning: `ຫົວໜ່ວຍບໍ່ສອດຄ່ອງກັບ targetUnit (ຄວາມຄິດ: ${expectedUnit}, ປ້ອນ: ${parsed.unit})`,
        parsed,
      };
    }
  }

  return {
    isValid: true,
    parsed,
  };
}

/**
 * Check if a criteria value is in standard format
 */
export function isStandardFormat(value: string | null | undefined): boolean {
  if (!value || value.trim() === "") return false;
  const parsed = parseCriteria(value);
  return !parsed.isCustom && parsed.operator !== null && parsed.value !== null;
}

/**
 * Get unit label for display
 */
export function getUnitLabel(unit: CriteriaUnit): string {
  switch (unit) {
    case "%":
      return "ເປີເຊັນ (%)";
    case "ຄັ້ງ":
      return "ຈຳນວນຄັ້ງ";
    case "ຄະແນນ":
      return "ຄະແນນ";
    case "ກີບ":
      return "ກີບ";
    case "":
      return "ບໍ່ມີຫົວໜ່ວຍ";
    default:
      return "";
  }
}
